'use client';

import { useMemo } from 'react';

import type { AbstractIntlMessages } from 'next-intl';
import { ThemeProvider } from 'next-themes';

import { I18nClientProvider } from '@ci/i18n/provider';
import { MonitoringProvider } from '@ci/monitoring/components';
import type { AccountRegion } from '@ci/shared/currency';
import { AppEventsProvider } from '@ci/shared/events';
import { CSPProvider } from '@ci/ui/csp-provider';
import { If } from '@ci/ui/if';
import { VersionUpdater } from '@ci/ui/version-updater';

import { AnalyticsProvider } from '~/components/analytics-provider';
import { AuthProvider } from '~/components/auth-provider';
import { IntercomLauncherController } from '~/components/intercom-launcher-controller';
import { ReleaseCelebration } from '~/components/release-celebration/release-celebration';
import appConfig from '~/config/app.config';
import featuresFlagConfig from '~/config/feature-flags.config';
import { mergeTerminology } from '~/i18n/terminology.utils';
import { LocaleMessagesSourceProvider } from '~/lib/i18n/locale-messages-source';

import { ReactQueryProvider } from './react-query-provider';

const EMPTY_MESSAGES: AbstractIntlMessages = {};

type RootProvidersProps = React.PropsWithChildren<{
  // The language to use for the app (optional)
  locale?: string;
  // The theme (light or dark or system) (optional)
  theme?: string;
  // The CSP nonce to pass to scripts (optional)
  nonce?: string;
  /**
   * The locale's messages UNRESOLVED (`$t(terms:…)` tokens intact). They are
   * resolved here, and kept as the source `TerminologyProvider` re-resolves
   * from when the account changes.
   */
  messages?: AbstractIntlMessages;
  /** The first-loaded account's terminology, for the root resolution. */
  terminology?: Record<string, string> | null;
  /** The first-loaded account's parsed region. */
  region?: AccountRegion | null;
}>;

export function RootProviders({
  locale = 'en',
  messages,
  terminology = null,
  region = null,
  theme = appConfig.theme,
  nonce,
  children,
}: RootProvidersProps) {
  const source = messages ?? EMPTY_MESSAGES;
  const resolvedMessages = useMemo(
    () => mergeTerminology(source, terminology, region, locale),
    [source, terminology, region, locale],
  );

  return (
    <MonitoringProvider>
      <AppEventsProvider>
        <AnalyticsProvider>
          <CSPProvider nonce={nonce}>
            <ReactQueryProvider>
              <LocaleMessagesSourceProvider messages={source}>
                <I18nClientProvider
                  locale={locale!}
                  messages={resolvedMessages}
                >
                  <AuthProvider>
                    <ThemeProvider
                      attribute="class"
                      enableSystem
                      disableTransitionOnChange
                      defaultTheme={theme}
                      enableColorScheme={false}
                      nonce={nonce}
                    >
                      <IntercomLauncherController />
                      {/* PAY-4392: around both navigation shells on purpose,
                          so the profile menu in either reaches "What's new". */}
                      <ReleaseCelebration>{children}</ReleaseCelebration>
                    </ThemeProvider>
                  </AuthProvider>

                  <If condition={featuresFlagConfig.enableVersionUpdater}>
                    <VersionUpdater />
                  </If>
                </I18nClientProvider>
              </LocaleMessagesSourceProvider>
            </ReactQueryProvider>
          </CSPProvider>
        </AnalyticsProvider>
      </AppEventsProvider>
    </MonitoringProvider>
  );
}
